/**
 * Single-select input — button-grid design (Step 6).
 *
 * Each option is a button; selected option gets an outline ring. The stored
 * value is the option's `option_key` (NOT its display label) so condition
 * engine evaluations remain stable across label edits (Step 7).
 */
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import type { SelectOption } from '@/features/form-builder/api/form-builder-api'
import type { InputProps } from '../input-props'

export function SingleSelectInput({
  field, value, onChange, onBlur, disabled, filteredOptions, invalid,
}: InputProps) {
  const allOptions = (field.type_config?.options as SelectOption[] | undefined) ?? []
  const options = filteredOptions
    ? allOptions.filter((o) => filteredOptions.includes(o.option_key))
    : allOptions

  if (options.length === 0) {
    return (
      <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2">
        گزینه‌ای تعریف نشده است
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5" onBlur={onBlur}>
      {options.map((opt) => {
        const selected = value === opt.option_key
        return (
          <button
            key={opt.option_key}
            type="button"
            onClick={() => {
              if (disabled) return
              onChange(selected ? null : opt.option_key)
            }}
            disabled={disabled}
            aria-pressed={selected}
            className={cn(
              'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm text-start transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              selected
                ? 'border-primary bg-primary/20 text-primary font-medium ring-1 ring-primary'
                : 'border-border bg-background text-foreground hover:border-foreground/40',
              invalid && !selected && 'border-destructive/50',
            )}
          >
            <span
              className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40',
              )}
              aria-hidden
            >
              {selected && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            <span className="flex-1">{opt.label?.fa || opt.option_key}</span>
          </button>
        )
      })}
    </div>
  )
}
